import React, { useState, useEffect } from 'react';
import { Horse } from './types';
import { loadHorsesData } from './horsesData';
import './PriceAdjustment.css';

const getBasePath = () => '/databaseOps/';

interface PriceRow {
  contractId: string;
  horseName: string;
  ownerName: string;
  currentRent: number;
  newRent: number;
  selected: boolean;
}

const PriceAdjustment: React.FC = () => {
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [adjustMode, setAdjustMode] = useState<'fixed' | 'percent'>('fixed');
  const [adjustValue, setAdjustValue] = useState<number>(0);

  useEffect(() => {
    loadHorsesData()
      .then(({ horses }) => {
        const priceRows: PriceRow[] = horses
          .filter((h: Horse) => h.contract && h.contract.active && !h.hidden)
          .map((h: Horse) => ({
            contractId: h.contract!.id,
            horseName: h.name,
            ownerName: h.contract!.owner_name,
            currentRent: h.contract!.box_rent,
            newRent: h.contract!.box_rent,
            selected: true
          }))
          .sort((a, b) => a.ownerName.localeCompare(b.ownerName));
        setRows(priceRows);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load data:', error);
        alert('Fehler beim Laden der Daten');
        setIsLoading(false);
      });
  }, []);

  const handleApplyAdjustment = () => {
    setRows(rows.map(row => {
      if (!row.selected) return row;
      let newRent: number;
      if (adjustMode === 'fixed') {
        newRent = row.currentRent + adjustValue;
      } else {
        newRent = row.currentRent * (1 + adjustValue / 100);
      }
      return { ...row, newRent: Math.round(newRent * 100) / 100 };
    }));
  };

  const handleResetAll = () => {
    setRows(rows.map(row => ({ ...row, newRent: row.currentRent })));
    setAdjustValue(0);
  };

  const handleToggleAll = (selected: boolean) => {
    setRows(rows.map(row => ({ ...row, selected })));
  };

  const handleSave = async () => {
    const changed = rows.filter(r => r.newRent !== r.currentRent);
    if (changed.length === 0) {
      alert('Keine Preisänderungen vorhanden.');
      return;
    }

    const summary = changed
      .map(r => `${r.horseName} (${r.ownerName}): ${r.currentRent.toFixed(2)} → ${r.newRent.toFixed(2)} €`)
      .join('\n');

    if (!window.confirm(
      `${changed.length} Preisänderung(en) speichern?\n\n${summary}`
    )) return;

    setSaveStatus('saving');

    try {
      const response = await fetch(`${getBasePath()}adjustPrices.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          adjustments: changed.map(r => ({
            contract_id: r.contractId,
            new_rent: r.newRent
          }))
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save');
      }

      setSaveStatus('saved');
      // Update current rents to reflect saved state
      setRows(rows.map(r => ({ ...r, currentRent: r.newRent })));
      alert(`${result.updated} Preise erfolgreich aktualisiert.`);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error: any) {
      console.error('Error saving prices:', error);
      setSaveStatus('error');
      alert(`Fehler: ${error.message}`);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const changedCount = rows.filter(r => r.newRent !== r.currentRent).length;
  const totalDiff = rows.reduce((sum, r) => sum + (r.newRent - r.currentRent), 0);

  if (isLoading) {
    return (
      <div className="price-adjustment">
        <div style={{ padding: '2rem', textAlign: 'center' }}>Lade Vertragsdaten...</div>
      </div>
    );
  }

  return (
    <div className="price-adjustment">
      <header className="header">
        <h2>Preisanpassung</h2>
        <h4>{rows.length} aktive Verträge</h4>
      </header>

      <div className="adjust-controls">
        <div className="adjust-mode">
          <label>
            <input
              type="radio"
              checked={adjustMode === 'fixed'}
              onChange={() => setAdjustMode('fixed')}
            />
            Fester Betrag (€)
          </label>
          <label>
            <input
              type="radio"
              checked={adjustMode === 'percent'}
              onChange={() => setAdjustMode('percent')}
            />
            Prozentual (%)
          </label>
        </div>
        <div className="adjust-input">
          <input
            type="number"
            value={adjustValue}
            onChange={(e) => setAdjustValue(parseFloat(e.target.value) || 0)}
            step={adjustMode === 'fixed' ? '5' : '1'}
            placeholder={adjustMode === 'fixed' ? 'z.B. 20' : 'z.B. 5'}
          />
          <span>{adjustMode === 'fixed' ? '€' : '%'}</span>
        </div>
        <button onClick={handleApplyAdjustment} className="btn-apply">
          Auf ausgewählte anwenden
        </button>
        <button onClick={handleResetAll} className="btn-reset">
          Zurücksetzen
        </button>
      </div>

      {changedCount > 0 && (
        <div className="change-summary">
          {changedCount} Änderung(en) | Monatliche Differenz: {totalDiff >= 0 ? '+' : ''}{totalDiff.toFixed(2)} €
        </div>
      )}

      <div className="price-table-container">
        <table className="price-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={rows.every(r => r.selected)}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                />
              </th>
              <th>Pferd</th>
              <th>Besitzer</th>
              <th>Aktuelle Miete</th>
              <th>Neue Miete</th>
              <th>Differenz</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const diff = row.newRent - row.currentRent;
              return (
                <tr key={row.contractId} className={diff !== 0 ? 'row-changed' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.selected}
                      onChange={(e) => {
                        const updated = [...rows];
                        updated[idx] = { ...row, selected: e.target.checked };
                        setRows(updated);
                      }}
                    />
                  </td>
                  <td>{row.horseName}</td>
                  <td>{row.ownerName}</td>
                  <td>{row.currentRent.toFixed(2)} €</td>
                  <td>
                    <input
                      type="number"
                      value={row.newRent}
                      onChange={(e) => {
                        const updated = [...rows];
                        updated[idx] = { ...row, newRent: parseFloat(e.target.value) || 0 };
                        setRows(updated);
                      }}
                      step="5"
                      className="rent-input"
                    />
                  </td>
                  <td className={diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : ''}>
                    {diff !== 0 ? `${diff >= 0 ? '+' : ''}${diff.toFixed(2)} €` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="save-section">
        {saveStatus !== 'idle' && (
          <div className={`save-status save-status-${saveStatus}`}>
            {saveStatus === 'saving' && 'Speichert...'}
            {saveStatus === 'saved' && 'Gespeichert!'}
            {saveStatus === 'error' && 'Fehler beim Speichern'}
          </div>
        )}
        <button
          onClick={handleSave}
          className="btn-save"
          disabled={changedCount === 0 || saveStatus === 'saving'}
        >
          {changedCount} Preisänderung(en) speichern
        </button>
      </div>
    </div>
  );
};

export default PriceAdjustment;
